// Dev / staging template override (ARCHITECTURE §5.1 step 2, §10.3).
//
//   ?__template=arena                      → render this tab with Arena
//   ?__template=arena&__topt={"canvas":"night"}   (URL-encoded JSON options)
//   ?__template=                           → clear the override
//
// Persisted per tab in sessionStorage['scorelm:template-override'], so it
// survives in-app navigation and reloads. Honoured only in a Vite dev build
// or a build with VITE_SITE_TEMPLATE_OVERRIDE=1 (e2e, validation, staging);
// production builds compile it out. It is client-side only: it can never
// change what other fans see.

import type { RawOptions } from './options'

export const OVERRIDE_STORAGE_KEY = 'scorelm:template-override'
export const OVERRIDE_PARAM = '__template'
export const OVERRIDE_OPTIONS_PARAM = '__topt'

export interface TemplateOverride { id: string; options?: RawOptions }

export const DEV_OVERRIDE_ENABLED: boolean =
  import.meta.env.DEV || import.meta.env.VITE_SITE_TEMPLATE_OVERRIDE === '1'

function parseOptions(raw: string | null): RawOptions | undefined {
  if (!raw) return undefined
  try {
    const v = JSON.parse(raw)
    if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
    const out: RawOptions = {}
    for (const [k, val] of Object.entries(v)) if (typeof val === 'string' || typeof val === 'boolean') out[k] = val
    return out
  } catch {
    return undefined
  }
}

function readStored(): TemplateOverride | null {
  try {
    const raw = sessionStorage.getItem(OVERRIDE_STORAGE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as TemplateOverride
    return v && typeof v.id === 'string' && v.id ? v : null
  } catch {
    return null
  }
}

/**
 * The active override for a query string: `?__template=` wins (empty clears),
 * else the tab's stored one. Pure: no storage writes (persistDevOverride does
 * that, from an effect). Null when overrides are disabled.
 */
export function readDevOverride(search: string = typeof window !== 'undefined' ? window.location.search : ''): TemplateOverride | null {
  if (!DEV_OVERRIDE_ENABLED) return null
  const params = new URLSearchParams(search)
  if (params.has(OVERRIDE_PARAM)) {
    const id = (params.get(OVERRIDE_PARAM) ?? '').trim()
    return id ? { id, options: parseOptions(params.get(OVERRIDE_OPTIONS_PARAM)) } : null
  }
  return readStored()
}

/** Persist (or clear) `?__template=` for this tab, so the override survives in-app navigation and reloads. */
export function persistDevOverride(search: string): void {
  if (!DEV_OVERRIDE_ENABLED) return
  const params = new URLSearchParams(search)
  if (!params.has(OVERRIDE_PARAM)) return
  const next = readDevOverride(search)
  try {
    if (next) sessionStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(next))
    else sessionStorage.removeItem(OVERRIDE_STORAGE_KEY)
  } catch {
    // sessionStorage unavailable: the override applies to this URL only.
  }
}
